import { useState } from "react";
import { motion } from "framer-motion";
import { ChevronDown, Eye, Shield, Leaf, Home, Truck, Dog, Mail, FileText, Users, Star, ShoppingCart, Car, Wrench, CloudRain, Anchor, Clock } from "lucide-react";

const MAISON_BG = "https://customer-assets.emergentagent.com/job_villa-surveillance/artifacts/o2lp7o3p_maison.jpg";

const offers = [
  {
    id: "veille",
    name: "AŪRA Veille",
    subtitle: "Surveillance Essentielle",
    items: [
      { icon: Eye, label: "Visite de contrôle", desc: "Contrôle des accès et sécurité générale" },
      { icon: Home, label: "Maison", desc: "Aération et vérification eau, électricité et anomalies visibles" },
      { icon: Mail, label: "Courrier", desc: "Relevé et mise en sécurité du courrier" },
      { icon: FileText, label: "Compte rendu", desc: "Compte rendu simple après visite" },
    ],
    animalNote: null,
  },
  {
    id: "serenite",
    name: "AŪRA Sérénité",
    subtitle: "Gestion Complète",
    items: [
      { icon: Eye, label: "Visite de contrôle", desc: "Contrôle des accès et sécurité générale" },
      { icon: Home, label: "Maison", desc: "Aération, surveillance et contrôle générale de la propriété" },
      { icon: Dog, label: "Animaux", desc: "Gestion simple des animaux*" },
      { icon: Leaf, label: "Jardin", desc: "Arrosage du jardin ou des plantes" },
      { icon: Mail, label: "Courrier", desc: "Gestion du courrier et des colis" },
      { icon: FileText, label: "Compte rendu", desc: "Compte rendu régulier au propriétaire" },
      { icon: Users, label: "Coordination ponctuelle", desc: "Ménage, jardinier ou maintenance si besoin (prestations externes facturées en supplément)" },
    ],
    animalNote: {
      title: "* Gestion simple des animaux :",
      included: "Nourrissage, eau, entretien simple de l'espace de vie, vérification visuelle de l'animal",
      excluded: "Promenade ou sortie, soins ou traitement, présence prolongée et visites supplémentaires pour l'animal",
    },
  },
  {
    id: "prestige",
    name: "AŪRA Prestige",
    subtitle: "Service Premium Personnalisé",
    items: [
      { icon: Eye, label: "Visite de contrôle", desc: "Surveillance renforcée de la propriété" },
      { icon: Home, label: "Maison", desc: "Aération, surveillance et contrôle général de la propriété" },
      { icon: Dog, label: "Animaux", desc: "Gestion complète des animaux**" },
      { icon: Leaf, label: "Jardin", desc: "Arrosage et suivi du jardin" },
      { icon: Mail, label: "Courrier", desc: "Gestion du courrier et des colis" },
      { icon: FileText, label: "Compte rendu", desc: "Compte rendu complet et régulier au propriétaire" },
      { icon: Users, label: "Coordination des prestataires", desc: "Ménage, jardinier ou maintenance (prestations externes facturées en supplément)" },
      { icon: Home, label: "Préparation retour", desc: "Préparation de la maison avant votre retour" },
      { icon: ShoppingCart, label: "Courses d'arrivée", desc: "1 service de courses d'arrivée inclus (achats remboursés sur justificatifs)" },
      { icon: Star, label: "Service prioritaire", desc: "Disponibilité étendue" },
      { icon: Clock, label: "Personnalisation", desc: "Adaptation personnalisée des services selon les besoins du propriétaire" },
    ],
    animalNote: {
      title: "** Gestion complète des animaux :",
      included: "Nourrissage, eau, entretien simple de l'espace de vie, promenade ou sortie courte, temps de présence rassurant lors de la visite",
      excluded: "Soins vétérinaires, pension ou garde permanente, présence continue sur la propriété",
    },
  },
];

const complementaryServices = [
  "Courses d'arrivée",
  "Navette aéroport",
  "Gestion de véhicule (démarrage moteur)",
  "Gestion complète des animaux",
  "Coordination ménage",
  "Coordination jardinier",
  "Visite d'urgence / intempéries",
  "Présence lors d'une intervention technique",
  "Surveillance bateau / annexe",
];

function OfferCard({ offer, isOpen, onToggle }) {
  return (
    <div
      data-testid={`offer-card-${offer.id}`}
      className={`group bg-[#FAF8F5]/80 backdrop-blur-sm border transition-all duration-500 cursor-pointer ${
        isOpen
          ? "border-[#b39a73] shadow-xl"
          : "border-[#b39a73]/20 hover:border-[#b39a73] hover:shadow-xl hover:-translate-y-[3px]"
      }`}
      onClick={onToggle}
    >
      <div className="p-8 md:p-10">
        <h3
          className="text-2xl md:text-3xl text-[#2E3A34] mb-2 tracking-[0.03em]"
          style={{ fontFamily: "'Cinzel', serif" }}
        >
          {offer.name}
        </h3>
        <p
          className="text-[#b39a73] text-lg mb-6 italic"
          style={{ fontFamily: "'Cormorant Garamond', serif" }}
        >
          {offer.subtitle}
        </p>

        <div className="flex items-center justify-end">
          <ChevronDown
            className={`h-5 w-5 text-[#b39a73] transition-transform duration-300 ${
              isOpen ? "rotate-180" : ""
            }`}
          />
        </div>
      </div>

      <div
        className={`overflow-hidden transition-all duration-500 ease-in-out ${
          isOpen ? "max-h-[1000px] opacity-100" : "max-h-0 opacity-0"
        }`}
      >
        <div className="px-8 md:px-10 pb-8 md:pb-10 border-t border-[#b39a73]/20 pt-6">
          <ul className="space-y-4">
            {offer.items.map((item, i) => {
              const Icon = item.icon;
              return (
                <li key={i} className="flex items-start gap-4">
                  <Icon className="h-5 w-5 text-[#b39a73] mt-1 shrink-0" strokeWidth={1.5} />
                  <div>
                    <span
                      className="text-[#2E3A34] font-semibold text-base"
                      style={{ fontFamily: "'Cormorant Garamond', serif" }}
                    >
                      {item.label}
                    </span>
                    <span className="text-[#4E4A45] text-base" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                      {" — "}{item.desc}
                    </span>
                  </div>
                </li>
              );
            })}
          </ul>

          {offer.animalNote && (
            <div className="mt-6 pt-4 border-t border-[#b39a73]/10 text-sm text-[#4E4A45]/80" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              <p className="font-semibold text-[#4E4A45] mb-1">{offer.animalNote.title}</p>
              <p>— Inclus : {offer.animalNote.included}</p>
              <p>— Non inclus : {offer.animalNote.excluded}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function Offers() {
  const [openOffer, setOpenOffer] = useState(null);
  const [showComplementary, setShowComplementary] = useState(false);

  return (
    <section id="offres" data-testid="offers-section" className="relative py-24 md:py-32">
      {/* Background image */}
      <div
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: `url(${MAISON_BG})` }}
      />
      <div className="absolute inset-0 bg-[#2E3A34]/60" />

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          className="text-center mb-16"
        >
          <div className="flex items-center justify-center gap-6 mb-4">
            <div className="h-[1px] w-16 bg-[#b39a73]" />
            <h2
              className="text-3xl md:text-4xl text-white tracking-[0.05em]"
              style={{ fontFamily: "'Cinzel', serif" }}
              data-testid="offers-title"
            >
              Nos Offres
            </h2>
            <div className="h-[1px] w-16 bg-[#b39a73]" />
          </div>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8 mb-12">
          {offers.map((offer, index) => (
            <motion.div
              key={offer.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.6, delay: index * 0.15, ease: "easeOut" }}
            >
              <OfferCard
                offer={offer}
                isOpen={openOffer === offer.id}
                onToggle={() => setOpenOffer(openOffer === offer.id ? null : offer.id)}
              />
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-50px" }}
          transition={{ duration: 0.6, ease: "easeOut" }}
        >
          <div
            data-testid="complementary-services"
            className={`bg-[#FAF8F5]/80 backdrop-blur-sm border transition-all duration-500 cursor-pointer ${
              showComplementary
                ? "border-[#b39a73] shadow-xl"
                : "border-[#b39a73]/20 hover:border-[#b39a73] hover:shadow-lg"
            }`}
            onClick={() => setShowComplementary(!showComplementary)}
          >
            <div className="p-8 md:p-10 flex items-center justify-between">
              <div>
                <h3
                  className="text-2xl md:text-3xl text-[#2E3A34] tracking-[0.03em]"
                  style={{ fontFamily: "'Cinzel', serif" }}
                >
                  Services Complémentaires
                </h3>
                <p
                  className="text-[#b39a73] text-lg mt-1 italic"
                  style={{ fontFamily: "'Cormorant Garamond', serif" }}
                >
                  À la carte, selon vos besoins
                </p>
              </div>
              <ChevronDown
                className={`h-6 w-6 text-[#b39a73] transition-transform duration-300 shrink-0 ${
                  showComplementary ? "rotate-180" : ""
                }`}
              />
            </div>

            <div
              className={`overflow-hidden transition-all duration-500 ease-in-out ${
                showComplementary ? "max-h-[800px] opacity-100" : "max-h-0 opacity-0"
              }`}
            >
              <div className="px-8 md:px-10 pb-8 md:pb-10 border-t border-[#b39a73]/20 pt-6">
                <ul className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {complementaryServices.map((service, i) => (
                    <li
                      key={i}
                      className="flex items-center gap-3 py-2 text-[#4E4A45] text-base"
                      style={{ fontFamily: "'Cormorant Garamond', serif" }}
                    >
                      <div className="w-1.5 h-1.5 rounded-full bg-[#b39a73] shrink-0" />
                      {service}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
